import React from 'react';
import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const PluginVibeConfirmationDialog = ({
  open,
  onClose,
  t,
  pluginName,
  vibeAnalysis,
  vibeRequest,
  vibeLoading,
  clarificationAnswers = {},
  onClarificationAnswerChange,
  onConfirm,
}) => {
  const [draftAnswers, setDraftAnswers] = useState({});
  const previousOpen = useRef(open);
  const previousAnalysis = useRef(vibeAnalysis);
  const clarifyingQuestions = Array.isArray(vibeAnalysis?.clarifying_questions)
    ? vibeAnalysis.clarifying_questions
    : [];

  useEffect(() => {
    const dialogOpened = open && !previousOpen.current;
    const analysisChanged = vibeAnalysis !== previousAnalysis.current;
    if (open && (dialogOpened || analysisChanged)) {
      setDraftAnswers(clarificationAnswers || {});
    }
    previousOpen.current = open;
    previousAnalysis.current = vibeAnalysis;
  }, [open, vibeAnalysis, clarificationAnswers]);

  const blockingReasons = Array.isArray(vibeAnalysis?.blocking_reasons)
    ? vibeAnalysis.blocking_reasons
    : [];
  const hasBlockingReasons = blockingReasons.length > 0;
  const requiresClarification = clarifyingQuestions.length > 0;
  const hasAllRequiredAnswers = clarifyingQuestions.length > 0
    ? clarifyingQuestions.every((_, index) => String(draftAnswers?.[index] || '').trim())
    : vibeAnalysis?.can_proceed !== false;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!vibeLoading) {
          onClose();
        }
      }}
      fullWidth
      maxWidth="sm"
    >
    <DialogTitle>
      {t('global_setting.plugins_vibe_confirm_title', 'Review AI understanding')}
    </DialogTitle>
    <DialogContent>
      <Stack spacing={1.5}>
        <Typography>
          {t(
            'global_setting.plugins_vibe_confirm_message',
            'The AI read the current action "{{name}}" and reformulated your request below. Review its understanding before any code is generated.',
            { name: pluginName }
          )}
        </Typography>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            backgroundColor: '#eff6ff',
            border: '1px solid #bfdbfe',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            {t('global_setting.plugins_vibe_understanding_label', 'What the AI understands')}
          </Typography>
          <Typography variant="body2">
            {vibeAnalysis?.understanding || ''}
          </Typography>
        </Box>
        {vibeAnalysis?.current_behavior ? (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {t('global_setting.plugins_vibe_current_behavior', 'Current behavior')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {vibeAnalysis.current_behavior}
            </Typography>
          </Box>
        ) : null}
        {vibeAnalysis?.requested_behavior ? (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {t('global_setting.plugins_vibe_requested_behavior', 'Requested behavior')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {vibeAnalysis.requested_behavior}
            </Typography>
          </Box>
        ) : null}
        {Array.isArray(vibeAnalysis?.proposed_changes) && vibeAnalysis.proposed_changes.length > 0 ? (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {t('global_setting.plugins_vibe_proposed_changes', 'Proposed changes')}
            </Typography>
            <List dense disablePadding>
              {vibeAnalysis.proposed_changes.map((change, index) => (
                <ListItem key={`${change}-${index}`} disableGutters sx={{ py: 0 }}>
                  <ListItemText primary={`• ${change}`} />
                </ListItem>
              ))}
            </List>
          </Box>
        ) : null}
        {Array.isArray(vibeAnalysis?.potential_risks) && vibeAnalysis.potential_risks.length > 0 ? (
          <Alert severity="warning" sx={{ py: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {t('global_setting.plugins_vibe_risks', 'Potential risks')}
            </Typography>
            <List dense disablePadding>
              {vibeAnalysis.potential_risks.map((risk, index) => (
                <ListItem key={`${risk}-${index}`} disableGutters sx={{ py: 0 }}>
                  <ListItemText primary={`• ${risk}`} />
                </ListItem>
              ))}
            </List>
          </Alert>
        ) : null}
        {hasBlockingReasons ? (
          <Alert severity="error" sx={{ py: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {t('global_setting.plugins_vibe_blockers', 'Cannot continue yet')}
            </Typography>
            <List dense disablePadding>
              {blockingReasons.map((reason, index) => (
                <ListItem key={`${reason}-${index}`} disableGutters sx={{ py: 0 }}>
                  <ListItemText primary={`• ${reason}`} />
                </ListItem>
              ))}
            </List>
          </Alert>
        ) : null}
        {clarifyingQuestions.length > 0 ? (
          <Alert severity="info" sx={{ py: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {t('global_setting.plugins_vibe_questions', 'Questions to consider')}
            </Typography>
            <List dense disablePadding>
              {clarifyingQuestions.map((question, index) => (
                <ListItem key={`${question}-${index}`} disableGutters sx={{ py: 0.75, display: 'block' }}>
                  <ListItemText primary={`• ${question}`} sx={{ mb: 0.5 }} />
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    required={requiresClarification}
                    label={t('global_setting.plugins_vibe_answer_label', 'Your answer')}
                    placeholder={t('global_setting.plugins_vibe_answer_placeholder', 'Answer this question to clarify the requested change')}
                    value={draftAnswers?.[index] || ''}
                    onChange={(event) => {
                      const value = event.target.value;
                      setDraftAnswers((currentAnswers) => ({
                        ...currentAnswers,
                        [index]: value,
                      }));
                      if (typeof onClarificationAnswerChange === 'function') {
                        onClarificationAnswerChange(index, value);
                      }
                    }}
                  />
                </ListItem>
              ))}
            </List>
            {requiresClarification && !hasAllRequiredAnswers ? (
              <Typography variant="caption" color="text.secondary">
                {t('global_setting.plugins_vibe_answer_required', 'Answer each question before generating the code.')}
              </Typography>
            ) : null}
          </Alert>
        ) : null}
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            {t('global_setting.plugins_vibe_confirm_request_label', 'Your request')}
          </Typography>
          <Typography variant="body2">{vibeRequest}</Typography>
        </Box>
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button disabled={vibeLoading} onClick={onClose}>
        {t('global_setting.plugins_vibe_confirm_cancel', 'Cancel')}
      </Button>
      <Button
        variant="contained"
        startIcon={<AutoAwesomeIcon />}
        disabled={vibeLoading || !vibeAnalysis || hasBlockingReasons || !hasAllRequiredAnswers}
        onClick={() => onConfirm(draftAnswers)}
      >
        {requiresClarification
          ? t('global_setting.plugins_vibe_confirm_with_answers', 'Send answers and generate code')
          : t('global_setting.plugins_vibe_confirm_action', 'Confirm and generate code')}
      </Button>
    </DialogActions>
    </Dialog>
  );
};

export default PluginVibeConfirmationDialog;
